import React, { useState, useEffect } from 'react';
import BusinessAccountModal from '../components/BusinessAccountModal';
import './BusinessAccounts.css';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchBusinessAccounts, setCurrentAccount } from '../store/slices/businessAccountsSlice';

const BusinessAccounts = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const dispatch = useAppDispatch();
  const { accounts, currentAccount, loading } = useAppSelector(state => ({
    accounts: state.businessAccounts.accounts || [],
    currentAccount: state.businessAccounts.currentAccount || null,
    loading: state.businessAccounts.loading
  }));

  useEffect(() => {
    dispatch(fetchBusinessAccounts());
  }, [dispatch]);

  const handleAddAccount = () => {
    setEditingAccount(null);
    setIsModalOpen(true);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setIsModalOpen(true);
  };

  const handleSaveAccount = async (formData) => {
    // Server-side create/update not implemented in this repo. Re-fetch for demo.
    alert('Save operation will be performed on the server in production. This is a dev preview.');
    dispatch(fetchBusinessAccounts());
  };

  const getStatusBadge = (status) => {
    const config = {
      'APPROVED': { color: '#10b981', bg: '#d1fae5' },
      'PENDING': { color: '#f59e0b', bg: '#fef3c7' },
      'REJECTED': { color: '#ef4444', bg: '#fee2e2' },
      'LIMITED': { color: '#6366f1', bg: '#e0e7ff' }
    };

    const statusConfig = config[status] || config['PENDING'];
    return (
      <span 
        className="status-badge" 
        style={{ background: statusConfig.bg, color: statusConfig.color }}
      >
        {status}
      </span>
    );
  };

  const getVerificationBadge = (status) => {
    const config = {
      'VERIFIED': { color: '#10b981', bg: '#d1fae5' },
      'PENDING': { color: '#f59e0b', bg: '#fef3c7' },
      'NOT_VERIFIED': { color: '#6b7280', bg: '#f3f4f6' },
      'REJECTED': { color: '#ef4444', bg: '#fee2e2' }
    };

    const statusConfig = config[status] || config['NOT_VERIFIED'];
    return (
      <span 
        className="verification-badge" 
        style={{ background: statusConfig.bg, color: statusConfig.color }}
      >
        {status.replace('_', ' ')}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stats = {
    total: accounts.length,
    approved: accounts.filter(a => a.accountStatus === 'APPROVED').length,
    verified: accounts.filter(a => a.businessVerificationStatus === 'VERIFIED').length,
    totalPhoneNumbers: accounts.reduce((sum, a) => sum + a.phoneNumbers, 0),
    totalPartners: accounts.reduce((sum, a) => sum + (a.partnerCount || 0), 0),
    multiPartnerAccounts: accounts.filter(a => (a.partnerCount || 0) > 1).length
  };

  return (
    <div className="business-accounts-container">
      <div className="business-accounts-header">
        <div className="header-content">
          <h1>Business Accounts (WABA)</h1>
          <p>Manage your WhatsApp Business Accounts and monitor their status</p>
        </div>
        <button className="btn-add-account" onClick={handleAddAccount}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Account
        </button>
      </div>

      <div className="accounts-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Accounts</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.approved}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.verified}</div>
          <div className="stat-label">Verified</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalPhoneNumbers}</div>
          <div className="stat-label">Phone Numbers</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalPartners}</div>
          <div className="stat-label">Total Partners</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.multiPartnerAccounts}</div>
          <div className="stat-label">Multi-Partner Accounts</div>
        </div>
      </div>

      <div className="accounts-grid">
        {accounts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏢</div>
            <h3>No business accounts found</h3>
            <p>Create your first WhatsApp Business Account</p>
            <button className="btn-add-first" onClick={handleAddAccount}>
              Add Account
            </button>
          </div>
        ) : (
          accounts.map(account => (
            <div 
              key={account.id} 
              className={`account-card ${account.isDefault ? 'default' : ''} ${selectedAccount?.id === account.id ? 'selected' : ''}`}
              onClick={() => {
              setSelectedAccount(selectedAccount?.id === account.id ? null : account);
              dispatch(setCurrentAccount(selectedAccount?.id === account.id ? null : account));
            }}
            >
              <div className="account-header">
                <div className="account-title">
                  <h3>{account.name}</h3>
                  {account.isDefault && (
                    <span className="default-badge">Default</span>
                  )}
                </div>
                <button
                  className="action-btn edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditAccount(account);
                  }}
                  title="Edit Account"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </div>

              <div className="account-body">
                <div className="account-id">
                  <span className="id-label">WABA ID:</span>
                  <span className="id-value">{account.id}</span>
                </div>

                <div className="account-statuses">
                  <div className="status-item">
                    <span className="status-label">Account Status:</span>
                    {getStatusBadge(account.accountStatus)}
                  </div>
                  <div className="status-item">
                    <span className="status-label">Verification:</span>
                    {getVerificationBadge(account.businessVerificationStatus)}
                  </div>
                </div>

                <div className="account-info">
                  <div className="info-item">
                    <span className="info-label">Owner:</span>
                    <span className="info-value">{account.ownerBusinessName}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Namespace:</span>
                    <span className="info-value namespace">{account.messageTemplateNamespace}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Phone Numbers:</span>
                    <span className="info-value">{account.phoneNumbers}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Currency:</span>
                    <span className="info-value">{account.currency}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Timezone:</span>
                    <span className="info-value">{account.timezoneId}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Created:</span>
                    <span className="info-value">{formatDate(account.createdAt)}</span>
                  </div>
                </div>

                {(account.partners && account.partners.length > 0) && (
                  <div className="account-partners">
                    <div className="partners-header">
                      <span className="partners-label">Solution Partners ({account.partnerCount || 0}):</span>
                    </div>
                    <div className="partners-list">
                      {account.partners.map((partner, idx) => (
                        <div key={idx} className="partner-badge">
                          <span className="partner-name">{partner.name}</span>
                          <span className={`partner-role ${partner.role.toLowerCase()}`}>
                            {partner.role === 'PRIMARY' ? 'Primary' : 'Secondary'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <BusinessAccountModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAccount(null);
        }}
        onSave={handleSaveAccount}
        account={editingAccount}
      />
    </div>
  );
};

export default BusinessAccounts;

